import { callApi } from '@/api/api.config';
import { PrimaryButton } from '@/components/buttons/button.component';
import { cn } from '@/lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

interface LogoUploaderProps {
  customerId: string;
  setPresignedLogoUrl: (url: string) => void;
  existingLogo?: File;
  setFile: (file: File | undefined) => void;
  setIsFetchingPresignedLink: (isFetchingPresignedLink: boolean) => void;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  customerId,
  setPresignedLogoUrl,
  existingLogo,
  setFile,
  setIsFetchingPresignedLink,
}) => {
  const [preview, setPreview] = useState<string>('');
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file as data URL'));
      reader.readAsDataURL(file);
    });
  }

  useEffect(() => {
    if (existingLogo) {
      setSelectedFileName(existingLogo.name);
      fileToDataUrl(existingLogo)
        .then(setPreview)
        .catch(() => setPreview(''));
    } else {
      setSelectedFileName('');
      setPreview('');
    }
  }, [existingLogo]);

  const validateFile = (file: File): boolean => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      toast.error('Error: Incorrect filetype. Only PNG, JPEG, and SVG files are allowed.');
      setError('Only PNG, JPEG, and SVG files are allowed.');
      return false;
    }
    if (file.size > maxSize) {
      toast.error('Error: Filesize error. Files must be under 5MB.');
      setError('File size must be under 5MB.');
      return false;
    }
    return true;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!selectedFile) return;

    if (validateFile(selectedFile)) {
      setSelectedFileName(selectedFile.name);
      setFile(selectedFile);
      fileToDataUrl(selectedFile)
        .then(setPreview)
        .catch(() => setPreview(''));
    }

    setIsFetchingPresignedLink(true);

    try {
      const response = await callApi({
        method: 'POST',
        url: `customer/logo/${customerId}/`,
      });

      if (response.status === 200) {
        setPresignedLogoUrl(response.data.logoUrl);
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Failed to fetch presigned logo URL. Please try again.');
    } finally {
      setIsFetchingPresignedLink(false);
    }
  };

  const handleRemove = () => {
    setFile(undefined);
    setSelectedFileName('');
    setPreview('');
    setPresignedLogoUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <p className={cn('text-sm font-medium text-gray-600')}>Edit Logo</p>
      <div className={cn('mt-2 border p-4 rounded-md w-full flex flex-col bg-neutral-0')}>
        {/* Hidden native file input — triggered by the custom button below */}
        <input
          ref={fileInputRef}
          type="file"
          id="logo-upload"
          accept="image/png, image/jpeg, image/svg+xml"
          onChange={handleFileChange}
          disabled={!customerId}
          className="hidden"
        />

        {/* Custom choose file button + filename display */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={!customerId}
            title={!customerId ? 'Select a customer before uploading a logo' : ''}
            className={cn(
              'py-3 px-4 rounded-lg border font-medium text-base',
              'hover:bg-neutral-200 hover:cursor-pointer',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            Choose File
          </button>
          <span className={cn('text-sm text-gray-600 truncate', !selectedFileName && 'text-gray-400')}>
            {selectedFileName || 'No file selected'}
          </span>
        </div>

        {error && <p className="text-red-500 mt-2">{error}</p>}

        {preview && (
          <div className="mt-4 flex flex-col items-start w-28">
            <div className="w-full flex justify-center">
              <img
                src={preview}
                alt="Logo Preview"
                className="w-full h-24 object-contain border rounded"
                onError={(e) => {
                  console.error('Error loading image preview');
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = '';
                  setError('Failed to load image preview');
                }}
              />
            </div>
            <div className="w-full mt-4">
              <PrimaryButton
                type="button"
                onClick={handleRemove}
                variant="stretch"
                size="small"
                className="bg-error-500 hover:bg-error-600 text-neutral-0 rounded-lg px-4 py-3 text-base font-medium border-none"
              >
                Remove
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoUploader;
